import type React from "react";
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import { easeInOut } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";

interface ComingSoonFeatureProps {
  featureName?: string;
  title?: string;
  description?: string;
  height?: number | string;
  width?: number | string;
  skeletonType?: "list" | "card" | "table" | "calendar" | "dashboard";
  animated?: boolean;
  customSkeleton?: React.ReactNode;
}

const ComingSoonFeature: React.FC<ComingSoonFeatureProps> = ({
  featureName = "BOOKINGS",
  title,
  description = "This exciting feature is currently in development",
  height = "100vh",
  width = "100%",
  skeletonType = "dashboard",
  animated = true,
  customSkeleton,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Define colors from reduxTheme with fallbacks
  const primaryColorLight = reduxTheme.primary_colors?.[400] || "#9c6644";
  const primaryColorDark = reduxTheme.primary_colors?.[600] || "#6b4e3d";
  const primaryTextColor = reduxTheme.primary_colors?.[500] || "#724E41";
  const secondaryTextColor = reduxTheme.neutral_colors?.[800] || "#D6DDEB";
  const backgroundColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";
  const borderColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";

  const renderSkeletonContent = () => {
    if (customSkeleton) return customSkeleton;

    switch (skeletonType) {
      case "list":
        return (
          <Stack spacing={2}>
            {[...Array(5)].map((_, index) => (
              <Box key={index} display="flex" alignItems="center" gap={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Box>
            ))}
          </Stack>
        );
      case "card":
        return (
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
            gap={2}
          >
            {[...Array(4)].map((_, index) => (
              <Card key={index} variant="outlined" sx={{ borderColor }}>
                <Skeleton variant="rectangular" height={120} />
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      case "table":
        return (
          <Stack spacing={1}>
            <Box display="flex" gap={2} mb={2}>
              <Skeleton variant="text" width="20%" height={32} />
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton variant="text" width="25%" height={32} />
              <Skeleton variant="text" width="25%" height={32} />
            </Box>
            {[...Array(6)].map((_, index) => (
              <Box key={index} display="flex" gap={2}>
                <Skeleton variant="text" width="20%" />
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="25%" />
                <Skeleton variant="text" width="25%" />
              </Box>
            ))}
          </Stack>
        );
      case "calendar":
        return (
          <Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(7, 1fr)"
              gap={1}
              mb={2}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <Typography
                  key={day}
                  variant="caption"
                  textAlign="center"
                  fontWeight="bold"
                  sx={{ color: secondaryTextColor }}
                >
                  {day}
                </Typography>
              ))}
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
              {[...Array(35)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={32} />
              ))}
            </Box>
          </Box>
        );
      case "dashboard":
      default:
        return (
          <Stack spacing={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton variant="rectangular" width={120} height={36} />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
              gap={2}
            >
              {[...Array(3)].map((_, index) => (
                <Card key={index} variant="outlined" sx={{ borderColor }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" height={32} />
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Box>
              <Skeleton variant="text" width="25%" height={24} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={200} />
            </Box>
          </Stack>
        );
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const textVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, delay: 0.2 },
    },
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Number.POSITIVE_INFINITY,
        ease: easeInOut,
      },
    },
  };

  return (
    <motion.div
      initial={animated ? "hidden" : "visible"}
      animate="visible"
      variants={overlayVariants}
    >
      <Box
        sx={{
          position: "relative",
          width,
          height,
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${borderColor}`,
          backgroundColor: backgroundColor,
        }}
      >
        {/* Skeleton Background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            p: 3,
            opacity: 0.3,
            filter: "blur(1px)",
          }}
        >
          {renderSkeletonContent()}
        </Box>

        {/* Overlay */}
        <motion.div
          variants={overlayVariants}
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg, ${alpha(
              primaryColorLight,
              0.12
            )}, ${alpha(primaryColorDark, 0.12)})`,
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <motion.div
            variants={textVariants}
            animate={animated ? pulseVariants.pulse : undefined}
            style={{
              textAlign: "center",
              padding: 4, // Use numeric value for consistency
            }}
          >
            <Chip
              label="Coming Soon"
              sx={{
                mb: 2,
                fontWeight: "bold",
                fontSize: "0.75rem",
                letterSpacing: 1,
                color: primaryTextColor,
                backgroundColor: secondaryTextColor,
                border: `1px solid ${borderColor}`,
              }}
            />

            <Typography
              variant="h4"
              component="h2"
              fontWeight="bold"
              sx={{
                mb: 2,
                background: `linear-gradient(45deg, ${primaryColorLight}, ${primaryColorDark})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: 2,
              }}
            >
              {title || `${featureName} COMING SOON!`}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: secondaryTextColor,
                maxWidth: 300,
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          </motion.div>
        </motion.div>
      </Box>
    </motion.div>
  );
};

export default ComingSoonFeature;
